import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, TextInput, ActivityIndicator, Alert } from 'react-native';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { initiateRoadCall, cancelRoadCall } from '../db_calls_wrapper';

const ContactPad = () => {
  const { phoneNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const customerInfo = location.state || {};

  const [calling, setCalling] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [editablePhoneNumber, setEditablePhoneNumber] = useState(phoneNumber || '');
  const cancelTicketRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const formatPhoneDisplay = (value) => {
    // Format as xxx-xxx-xxxx
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    } else {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (text) => {
    const cleaned = text.replace(/\D/g, '');
    setEditablePhoneNumber(cleaned);
  };

  const isValidPhoneNumber = () => {
    const cleaned = editablePhoneNumber.replace(/\D/g, '');
    return cleaned.length === 10;
  };

  const handleCall = async () => {
    const cleaned = editablePhoneNumber.replace(/\D/g, '');
    if (!cleaned || cleaned.length !== 10) {
      Alert.alert('Invalid Number', 'Please enter a valid 10-digit phone number');
      return;
    }

    const confirmed = window.confirm(
      `Place Call?\nThis will ring your cell first, then connect to ${formatPhoneDisplay(cleaned)}.`
    );
    if (!confirmed) return;

    const ticket = { cancelled: false };
    cancelTicketRef.current = ticket;
    setCalling(true);

    const result = await initiateRoadCall({
      customerPhone: cleaned,
      customerName: customerName !== 'Customer' ? customerName : '',
    });

    if (ticket.cancelled) {
      if (result?.callSid) {
        cancelRoadCall({ callSid: result.callSid, customerPhone: cleaned }).catch(() => {});
      }
      return;
    }

    setCalling(false);
    if (!result?.success) {
      Alert.alert('Call Failed', result?.error || 'Could not start the call.');
    }
  };

  const handleCancelCalling = () => {
    if (cancelTicketRef.current) cancelTicketRef.current.cancelled = true;
    setCalling(false);
  };

  const handleKeypadPress = (digit) => {
    if (editablePhoneNumber.replace(/\D/g, '').length < 10) {
      setEditablePhoneNumber(prev => prev + digit);
    }
  };

  const handleBackspace = () => {
    setEditablePhoneNumber(prev => prev.slice(0, -1));
  };

  const customerName = customerInfo.fullName || 
    `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim() || 
    'Customer';

  return (
    <View style={styles.container}>
      {/* Floating Back Button */}
      <TouchableOpacity dataSet={{ hover: "lift" }}
        style={[
          styles.floatingBackButton,
          isDesktop && styles.floatingBackButtonDesktop
        ]}
        onPress={() => navigate(-1)}
      >
        <Text style={styles.floatingBackButtonText}>←</Text>
      </TouchableOpacity>

        <View style={isDesktop ? styles.desktopWrapper : null}>

        {/* Main Content */}
        <View style={styles.content}>
          {/* Customer Name */}
          <Text style={styles.customerName}>{customerName}</Text>

          {/* Phone Display */}
          <View style={styles.phoneDisplay}>
            <Text style={styles.phoneNumber}>
              {editablePhoneNumber ? formatPhoneDisplay(editablePhoneNumber) : '___-___-____'}
            </Text>
          </View>

          {/* Keypad */}
          <View style={styles.keypad}>
            {[['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9'], ['*', '0', '#']].map((row, rowIndex) => (
              <View key={rowIndex} style={styles.keypadRow}>
                {row.map((digit) => (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    key={digit}
                    style={styles.keypadButton}
                    onPress={() => handleKeypadPress(digit)}
                  >
                    <Text style={styles.keypadButtonText}>{digit}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ))}
            
            {/* Bottom row with backspace */}
            <View style={styles.keypadRow}>
              <View style={styles.keypadButton} />
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.keypadButton}
                onPress={handleBackspace}
              >
                <Text style={styles.keypadButtonText}>⌫</Text>
              </TouchableOpacity>
              <View style={styles.keypadButton} />
            </View>
          </View>

          {/* Call Control Buttons */}
          <View style={styles.callControls}>
            {/* Call Button */}
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[
                styles.callButton,
                (!isValidPhoneNumber() || calling) && styles.callButtonDisabled
              ]}
              onPress={handleCall}
              disabled={!isValidPhoneNumber() || calling}
            >
              {calling ? (
                <ActivityIndicator size="small" color="#2E3A38" />
              ) : (
                <Text style={[
                  styles.callButtonIcon,
                  (!isValidPhoneNumber() || calling) && styles.callButtonIconDisabled
                ]}>📞</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Blocking Modal - Calling */}
      <Modal
        visible={calling}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {}} // Prevent closing while initiating
      >
        <View style={styles.blockingModalOverlay}>
          <View style={styles.blockingModalContainer}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.blockingModalText}>
              Connecting to {customerName}. Your phone will ring shortly.
            </Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.cancelCallingButton}
              onPress={handleCancelCalling}
            >
              <Text style={styles.cancelCallingButtonText}>CANCEL</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  desktopWrapper: {
    width: '100%',
    maxWidth: 800,
    flex: 1,
    alignSelf: 'center',
  },
  floatingBackButton: {
    position: 'absolute',
    top: 20,
    left: 20,
    width: 50,
    height: 50,
    backgroundColor: 'rgba(0, 122, 255, 0.5)',
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  floatingBackButtonDesktop: {
    left: 'calc(50% - 400px + 20px)',
  },
  floatingBackButtonText: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    lineHeight: 28,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  customerName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 30,
    textAlign: 'center',
  },
  phoneDisplay: {
    alignItems: 'center',
    marginBottom: 30,
    backgroundColor: '#F4F6F6',
    paddingVertical: 20,
    paddingHorizontal: 40,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#0E7C6B',
    width: 350,
  },
  phoneNumber: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#2E3A38',
    letterSpacing: 2,
  },
  keypad: {
    marginBottom: 30,
  },
  keypadRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 12,
    gap: 20,
  },
  keypadButton: {
    width: 70,
    height: 70,
    alignItems: 'center',
    justifyContent: 'center',
  },
  keypadButtonText: {
    fontSize: 28,
    fontWeight: '600',
    color: '#2E3A38',
  },
  callControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 30,
    marginTop: 20,
  },
  callControlButton: {
    width: 60,
    height: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  callControlIcon: {
    fontSize: 28,
  },
  callButton: {
    width: 80,
    height: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  callButtonDisabled: {
    opacity: 0.3,
  },
  callButtonIcon: {
    fontSize: 36,
  },
  callButtonIconDisabled: {
    opacity: 0.3,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 500,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2E3A38',
  },
  closeButton: {
    fontSize: 24,
    color: '#5C6B68',
    fontWeight: 'bold',
  },
  inputGroup: {
    marginBottom: 15,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2E3A38',
    marginBottom: 5,
  },
  input: {
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#F4F6F6',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  saveButton: {
    backgroundColor: '#0E7C6B',
    padding: 15,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 10,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  blockingModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  blockingModalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 24,
    alignItems: 'center',
    minWidth: 200,
  },
  blockingModalText: {
    marginTop: 16,
    fontSize: 16,
    color: '#2E3A38',
    fontWeight: '500',
    textAlign: 'center',
  },
  cancelCallingButton: {
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 6,
    backgroundColor: '#F4F6F6',
  },
  cancelCallingButtonText: {
    color: '#2E3A38',
    fontSize: 14,
    fontWeight: 'bold',
  },
});

export default ContactPad;
